<template lang="pug">
  .d-flex.flex-column.pb-1
    label.ui-body-1.font-weight-medium.mb-2(v-if="label")
      | {{ label }}{{ required ? ' *' : '' }}
    v-sheet.position-relative(
      color="transparent"
      :min-height="containerMinHeight"
      :aria-busy="isLoading ? 'true' : 'false'"
      :aria-disabled="disabled ? 'true' : 'false'"
    )
      div(ref="container")
      v-overlay(
        v-if="isLoading"
        absolute
        color="transparent"
        opacity="0"
      )
        v-progress-circular(
          indeterminate
          color="ui-primary"
        )
      v-overlay(
        v-if="disabled"
        absolute
        color="transparent"
        opacity="0"
      )
    .mt-1(v-if="displayedMessages.length")
      p.ui-body-2.ui-error--text.mb-0(
        v-for="(message, index) in displayedMessages"
        :key="`${index}-${message}`"
        role="alert"
      ) {{ message }}
</template>

<script>
const READY_EVENT = 'yandex-smartcaptcha-ready';
const SCRIPT_ID = 'yandex-smartcaptcha-script';
const LANGUAGES = ['ru', 'en', 'be', 'kk', 'tt', 'uk', 'uz', 'tr'];
const SHIELD_POSITIONS = ['top-left', 'top-right', 'bottom-left', 'bottom-right'];

export default {
  name: 'YandexCaptchaField',
  props: {
    value: {
      type: String,
      default: '',
    },
    sitekey: {
      type: String,
      default: () => process.env.VUE_APP_YANDEX_CAPTCHA_SITE_KEY || 'ysc1_1YAGHeQmo2NXOfXqFN3lMLF2Tr3GmzeRnKlF92gZd06609ec' || '', // @remove key
    },
    label: {
      type: String,
      default: '',
    },
    errorMessages: {
      type: [String, Array],
      default: () => [],
    },
    language: {
      type: String,
      default: 'ru',
      validator: (value) => LANGUAGES.includes(value),
    },
    shieldPosition: {
      type: String,
      default: undefined,
      validator: (value) => value === undefined || SHIELD_POSITIONS.includes(value),
    },
    loadTimeout: {
      type: Number,
      default: 10000,
      validator: (value) => value > 0,
    },
    test: {
      type: Boolean,
      default: false,
    },
    webview: {
      type: Boolean,
      default: false,
    },
    invisible: {
      type: Boolean,
      default: false,
    },
    hideShield: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    required: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      widgetId: null,
      isLoading: false,
      internalError: '',
      initializationPromise: null,
      initializationVersion: 0,
      unsubscribeCallbacks: [],
      isMounted: false,
      isInternalValueUpdate: false,
    };
  },
  computed: {
    captchaApi() {
      return typeof window !== 'undefined' ? window.smartCaptcha : undefined;
    },
    containerMinHeight() {
      return this.invisible ? 0 : 100;
    },
    displayedMessages() {
      const externalMessages = Array.isArray(this.errorMessages)
        ? this.errorMessages
        : [this.errorMessages];

      return [this.internalError, ...externalMessages].filter(Boolean);
    },
    renderOptions() {
      const options = {
        sitekey: this.sitekey,
        callback: this.handleSuccess,
        hl: this.language,
        test: this.test,
        webview: this.webview,
        invisible: this.invisible,
        hideShield: this.hideShield,
      };

      if (this.shieldPosition) {
        options.shieldPosition = this.shieldPosition;
      }

      return options;
    },
    renderSignature() {
      return JSON.stringify({
        sitekey: this.sitekey,
        language: this.language,
        test: this.test,
        webview: this.webview,
        invisible: this.invisible,
        hideShield: this.hideShield,
        shieldPosition: this.shieldPosition,
      });
    },
  },
  watch: {
    value(value, previousValue) {
      if (!value && previousValue && !this.isInternalValueUpdate) {
        this.resetWidget();
      }
    },
    renderSignature() {
      if (this.isMounted) {
        this.reinitialize();
      }
    },
  },
  mounted() {
    this.isMounted = true;
    this.initialize();
  },
  beforeDestroy() {
    this.isMounted = false;
    this.destroy();
  },
  methods: {
    waitForProvider() {
      if (this.captchaApi) {
        return Promise.resolve(this.captchaApi);
      }

      return new Promise((resolve, reject) => {
        let timeoutId;
        const script = document.getElementById(SCRIPT_ID);
        const getCaptchaApi = () => this.captchaApi;
        let handleReady;
        let handleScriptError;
        const cleanup = () => {
          window.removeEventListener(READY_EVENT, handleReady);
          script?.removeEventListener('error', handleScriptError);
          window.clearTimeout(timeoutId);
        };
        handleReady = () => {
          const captchaApi = getCaptchaApi();
          if (!captchaApi) return;
          cleanup();
          resolve(captchaApi);
        };
        handleScriptError = () => {
          cleanup();
          reject(new Error('SmartCaptcha script failed to load'));
        };

        window.addEventListener(READY_EVENT, handleReady);
        script?.addEventListener('error', handleScriptError, { once: true });
        timeoutId = window.setTimeout(() => {
          cleanup();
          reject(new Error('SmartCaptcha initialization timed out'));
        }, this.loadTimeout);
      });
    },
    initialize() {
      if (this.widgetId !== null) return this.widgetId;
      if (this.initializationPromise) return this.initializationPromise;

      this.internalError = '';

      if (!this.sitekey) {
        const error = new Error('Yandex SmartCaptcha site key is not configured');
        this.reportError(error, 'Не задан ключ клиента Yandex SmartCaptcha');
        return null;
      }

      this.isLoading = true;
      const version = this.initializationVersion;
      const initialization = this.renderWidget(version)
        .catch((error) => {
          if (this.isMounted && version === this.initializationVersion) {
            this.reportError(error, 'Не удалось загрузить Yandex SmartCaptcha');
          }
          return null;
        })
        .finally(() => {
          if (this.initializationPromise === initialization) {
            this.initializationPromise = null;
            this.isLoading = false;
          }
        });

      this.initializationPromise = initialization;
      return initialization;
    },
    async renderWidget(version) {
      const captchaApi = await this.waitForProvider();

      if (!this.isMounted || version !== this.initializationVersion) return null;

      const widgetId = captchaApi.render(this.$refs.container, this.renderOptions);

      if (widgetId === undefined || widgetId === null) {
        throw new Error('SmartCaptcha did not return a widget id');
      }

      this.widgetId = widgetId;
      this.subscribeToWidgetEvents();
      this.$emit('ready', widgetId);
      return widgetId;
    },
    subscribeToWidgetEvents() {
      this.subscribe('token-expired', this.handleTokenExpired);
      this.subscribe('network-error', this.handleNetworkError);
      this.subscribe('javascript-error', this.handleJavaScriptError);
      this.subscribe('challenge-visible', () => this.$emit('challenge-visible'));
      this.subscribe('challenge-hidden', () => this.$emit('challenge-hidden'));
    },
    subscribe(event, callback) {
      const unsubscribe = this.captchaApi.subscribe(this.widgetId, event, callback);

      if (typeof unsubscribe === 'function') {
        this.unsubscribeCallbacks.push(unsubscribe);
      }
    },
    handleSuccess(token) {
      const value = token || '';
      this.internalError = '';
      this.emitValue(value);

      if (value) {
        this.$emit('success', value);
      }
    },
    handleTokenExpired() {
      this.emitValue('');
      this.$emit('token-expired');
    },
    handleNetworkError(error) {
      this.reportError(
        error || new Error('SmartCaptcha network error'),
        'Не удалось связаться с Yandex SmartCaptcha. Повторите попытку',
      );
    },
    handleJavaScriptError(error) {
      this.reportError(
        error || new Error('SmartCaptcha JavaScript error'),
        'Yandex SmartCaptcha временно недоступна',
      );
    },
    reportError(error, message) {
      this.internalError = message;
      this.emitValue('');
      this.$emit('error', error);
    },
    emitValue(value) {
      if (this.value === value) return;

      this.isInternalValueUpdate = true;
      this.$emit('input', value);
      this.$nextTick(() => {
        this.isInternalValueUpdate = false;
      });
    },
    resetWidget() {
      if (this.widgetId !== null && this.captchaApi) {
        this.captchaApi.reset(this.widgetId);
      }
    },
    reset() {
      this.internalError = '';
      this.resetWidget();
      this.emitValue('');
      this.$emit('reset');
    },
    async execute() {
      const widgetId = this.widgetId === null ? await this.initialize() : this.widgetId;

      if (widgetId === null || !this.captchaApi) return false;

      this.captchaApi.execute(widgetId);
      return true;
    },
    getResponse() {
      if (this.widgetId === null || !this.captchaApi) return '';
      return this.captchaApi.getResponse(this.widgetId) || '';
    },
    reinitialize() {
      this.destroy();
      this.$nextTick(() => {
        if (this.isMounted) {
          this.initialize();
        }
      });
    },
    destroy({ clearValue = true } = {}) {
      this.initializationVersion += 1;
      this.initializationPromise = null;
      this.isLoading = false;
      this.unsubscribeCallbacks.forEach((unsubscribe) => unsubscribe());
      this.unsubscribeCallbacks = [];

      if (this.widgetId !== null && this.captchaApi) {
        this.captchaApi.destroy(this.widgetId);
      }

      this.widgetId = null;

      if (clearValue) {
        this.emitValue('');
      }
    },
  },
};
</script>
